import { api } from "@shared/api/client";

export type GroupChatScope = "admin" | "rrhh";
export type GroupChatUser = { id: number; nombre: string; correo: string; rol: string };
export type GroupChatAttachment = {
  id: number;
  nombre: string;
  mime: string | null;
  size: number | null;
};
export type GroupChatMessage = {
  id: number;
  from: "me" | "other";
  emisor_id: number | null;
  emisor_nombre: string;
  mensaje: string;
  creado_en: string;
  adjunto: GroupChatAttachment | null;
  client_id?: string;
  client_order?: number;
  status?: "sending" | "sent" | "error";
};
export type GroupChatGroup = {
  id: number;
  nombre: string;
  creador_id: number | null;
  is_creator: boolean;
  messages_enabled: boolean;
  blocked_until: string | null;
  blocked_reason: string | null;
  unread_count: number;
  last_message: string;
  last_message_at: string | null;
  last_read_message_id: number | null;
  members: GroupChatUser[];
};

type GroupMessagesResponse = {
  group: Pick<GroupChatGroup, "id" | "nombre"> & {
    enabled: boolean;
    blocked_until: string | null;
    reason: string | null;
  };
  messages: GroupChatMessage[];
};

const base = (scope: GroupChatScope) =>
  scope === "admin" ? "/admin-panel/chat/groups" : "/rrhh/chat/groups";

export async function getGroupChats(scope: GroupChatScope): Promise<GroupChatGroup[]> {
  const { data } = await api.get<GroupChatGroup[]>(base(scope));
  return data;
}

export async function getGroupChatUsers(): Promise<GroupChatUser[]> {
  const { data } = await api.get<GroupChatUser[]>("/admin-panel/chat/groups/users");
  return data;
}

export async function getGroupMessages(
  scope: GroupChatScope,
  groupId: number,
  afterId?: number | null,
): Promise<GroupMessagesResponse> {
  const { data } = await api.get<GroupMessagesResponse>(`${base(scope)}/${groupId}/messages`, {
    params: afterId && afterId > 0 ? { after_id: afterId } : undefined,
  });
  return data;
}

export async function sendGroupMessage(
  scope: GroupChatScope,
  groupId: number,
  message: string,
  clientId: string,
  file?: File | null,
): Promise<GroupChatMessage> {
  const form = new FormData();
  if (message.trim()) form.append("message", message.trim());
  if (file) form.append("archivo", file);
  form.append("client_id", clientId);

  const { data } = await api.post<{
    client_id?: string | null;
    message: GroupChatMessage;
  }>(`${base(scope)}/${groupId}/messages`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return {
    ...data.message,
    client_id: data.client_id ?? clientId,
    status: "sent",
  };
}

export async function markGroupRead(scope: GroupChatScope, groupId: number): Promise<void> {
  await api.post(`${base(scope)}/${groupId}/read`);
}

export async function createGroupChat(nombre: string, userIds: number[]): Promise<number> {
  const { data } = await api.post<{ ok: boolean; group_id: number }>(
    "/admin-panel/chat/groups",
    { nombre, user_ids: userIds },
  );
  return data.group_id;
}

export async function renameGroupChat(groupId: number, nombre: string): Promise<void> {
  await api.put(`/admin-panel/chat/groups/${groupId}`, { nombre });
}

export async function addGroupMember(groupId: number, userId: number): Promise<void> {
  await api.post(`/admin-panel/chat/groups/${groupId}/members`, { user_id: userId });
}

export async function removeGroupMember(groupId: number, userId: number): Promise<void> {
  await api.delete(`/admin-panel/chat/groups/${groupId}/members/${userId}`);
}

export async function setGroupMessaging(
  groupId: number,
  mode: "active" | "indefinite" | "until",
  blockedUntil?: string | null,
): Promise<void> {
  await api.patch(`/admin-panel/chat/groups/${groupId}/messaging`, {
    mode,
    blocked_until: blockedUntil ?? null,
  });
}

export async function deleteGroupChat(groupId: number): Promise<void> {
  await api.delete(`/admin-panel/chat/groups/${groupId}`);
}

export async function downloadGroupAttachment(
  scope: GroupChatScope,
  attachment: GroupChatAttachment,
): Promise<void> {
  const response = await api.get(`${base(scope)}/attachments/${attachment.id}/download`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = attachment.nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
